// The data rules behind the gateway settings form's Scenarios mode, kept out of
// the component for the same reason jsonObjectValue.js is: these rewrite a
// settings row's live settings, so they are pure functions with tests rather than
// logic inside a React class that jest cannot import (src/i18n.js awaits at the
// top level).
//
// An xscraper settings row holds one object per action — payin_init on a payin
// row, payout_make on a payout row — beside its endpoint and the rest. An action
// gets its script from exactly one of three keys:
//
//     "code":        "…inline JS…"
//     "scenario":    "bank-x-payin"   (a scraper scenario, by name)
//     "scenario_id": 12               (a scraper scenario, by id)
//
// More than one is refused when the row is saved
// (TRIGGER_SCRAPER_ACTION_AMBIGUOUS), as is a reference to a scenario that does
// not exist (TRIGGER_SCRAPER_SCENARIO_NOT_FOUND). The action's other keys —
// `input` with its credentials, `proxy`, anything else — are not ours to touch.

export const SOURCE_KEYS = ['code', 'scenario', 'scenario_id'];

const isPlainObject = (value) =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/**
 * Keys of the top-level values that are scraper actions: objects carrying code,
 * scenario or scenario_id. In document order. Empty for anything that is not an
 * object — including the raw string an unparsed draft is held as.
 */
export const scraperActionKeys = (settings) =>
    isPlainObject(settings)
        ? Object.keys(settings).filter(
            (key) =>
                isPlainObject(settings[key]) &&
                SOURCE_KEYS.some((source) => has(settings[key], source))
        )
        : [];

/**
 * Where an action's script comes from:
 *
 *     { source: 'scenario', linkBy: 'id',   ref: 12 }
 *     { source: 'scenario', linkBy: 'name', ref: 'bank-x' }
 *     { source: 'code', code: '…' }
 *
 * plus `ambiguous` when it carries more than one source key. Such an action is
 * shown by its scenario reference, id first, but it is not valid as it stands;
 * any rewrite below leaves exactly one.
 */
export const actionSource = (action) => {
    const obj = isPlainObject(action) ? action : {};
    const ambiguous = SOURCE_KEYS.filter((key) => has(obj, key)).length > 1;

    if (has(obj, 'scenario_id')) {
        return { source: 'scenario', linkBy: 'id', ref: obj.scenario_id, ambiguous };
    }
    if (has(obj, 'scenario')) {
        return { source: 'scenario', linkBy: 'name', ref: obj.scenario, ambiguous };
    }
    return { source: 'code', code: obj.code, ambiguous };
};

/**
 * The scenario an action references, looked up in `scenarios` (the light list:
 * id, name). Null for inline code, or when the reference is not in the list —
 * a scenario that was deleted, or one this list does not hold.
 *
 * Ids are compared as text: the JSON may hold 12 or "12", and both reach the
 * same row.
 */
export const findLinkedScenario = (action, scenarios = []) => {
    const { source, linkBy, ref } = actionSource(action);
    if (source !== 'scenario' || ref === null || ref === undefined) return null;
    return (
        scenarios.find((scenario) =>
            linkBy === 'id' ? String(scenario.id) === String(ref) : scenario.name === ref
        ) || null
    );
};

/** Whether the action holds inline code someone wrote — what switching it to a scenario would throw away. */
export const hasInlineCode = (action) =>
    isPlainObject(action) &&
    has(action, 'code') &&
    action.code !== null &&
    action.code !== undefined &&
    String(action.code).trim() !== '';

/**
 * The action with `sourceKey: value` as its only source key, every other key
 * kept. The new key takes the place of the first source key the action had, so
 * the JSON reads the same apart from that one line.
 */
const withSource = (action, sourceKey, value) => {
    const out = {};
    let placed = false;
    Object.entries(action).forEach(([key, current]) => {
        if (SOURCE_KEYS.includes(key)) {
            if (!placed) {
                out[sourceKey] = value;
                placed = true;
            }
            return;
        }
        out[key] = current;
    });
    return placed ? out : { [sourceKey]: value, ...out };
};

/**
 * The settings with `key`'s action replaced. Spread keeps the key where it was,
 * since it already exists; every other top-level key is the same value as before.
 */
const replaceAction = (settings, key, action) => ({ ...settings, [key]: action });

/**
 * Point `key`'s action at `scenario` ({id, name}), by id or by name, dropping its
 * inline code and any other reference.
 *
 * By name reads better in the JSON and lets one settings block be copied
 * between environments whose ids differ; by id does not depend on the name.
 * Either way the database checks the reference when the settings are saved.
 *
 * Returns `settings` unchanged when there is no such action or no scenario.
 */
export const linkActionToScenario = (settings, key, scenario, linkBy = 'id') => {
    const action = isPlainObject(settings) ? settings[key] : undefined;
    if (!isPlainObject(action) || !scenario) return settings;

    return linkBy === 'name'
        ? replaceAction(settings, key, withSource(action, 'scenario', scenario.name))
        : replaceAction(settings, key, withSource(action, 'scenario_id', scenario.id));
};

/**
 * Make `key`'s action run inline `code` again, dropping its scenario reference.
 * Returns `settings` unchanged when there is no such action.
 */
export const setActionInlineCode = (settings, key, code = '') => {
    const action = isPlainObject(settings) ? settings[key] : undefined;
    if (!isPlainObject(action)) return settings;

    return replaceAction(settings, key, withSource(action, 'code', code));
};
